import { MapPin, Clock, Phone, Leaf, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { RESTAURANT } from "@/data/menu";

const HOURS = [
  ["Monday – Sunday", "6:30 AM – 9:15 PM"],
  ["Breakfast & Tiffin", "from 6:30 AM"],
  ["South Indian Meals", "from 12:00 PM"],
  ["Indo-Chinese", "from 2:30 PM"],
];

export function Visit() {
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${RESTAURANT.mapsQuery}`;
  const mapEmbed = `https://www.google.com/maps?q=${RESTAURANT.mapsQuery}&output=embed`;

  return (
    <section id="visit" data-testid="visit-section" className="relative py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="font-body text-xs uppercase tracking-[0.35em] text-terracotta">
            Visit Us
          </span>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight mt-4 mb-16">
              Drop by <span className="italic text-olive">anytime.</span>
            </h2>
            <a
              data-testid="google-reviews-badge"
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 mb-16 rounded-full border border-line bg-surface px-5 py-3 hover:border-olive/50 hover:bg-cream transition-colors duration-300 shrink-0"
            >
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={15}
                    className={
                      i < Math.round(RESTAURANT.googleRating)
                        ? "fill-terracotta text-terracotta"
                        : "fill-line text-line"
                    }
                  />
                ))}
              </div>
              <span className="font-body text-sm text-ink/80">
                <span className="font-semibold text-ink">{RESTAURANT.googleRating}</span> on Google ·{" "}
                {RESTAURANT.googleReviewCount} reviews
              </span>
            </a>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Details */}
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="flex gap-4">
                <MapPin className="text-olive shrink-0 mt-1" size={22} />
                <div>
                  <p className="font-body text-xs uppercase tracking-widest text-ink/45 mb-2">
                    Address
                  </p>
                  <address className="not-italic font-body text-ink/80 leading-relaxed">
                    {RESTAURANT.addressLines.map((l) => (
                      <span key={l} className="block">{l}</span>
                    ))}
                  </address>
                  <a
                    data-testid="visit-directions-link"
                    href={mapsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-4 font-body text-sm font-semibold text-olive border-b border-olive/40 hover:border-olive transition-colors"
                  >
                    Get directions →
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="flex gap-4">
                <Clock className="text-olive shrink-0 mt-1" size={22} />
                <div className="w-full">
                  <p className="font-body text-xs uppercase tracking-widest text-ink/45 mb-3">
                    Opening Hours
                  </p>
                  <ul className="space-y-2.5">
                    {HOURS.map(([k, v]) => (
                      <li key={k} className="flex items-baseline justify-between border-b border-line pb-2">
                        <span className="font-body text-sm text-ink/70">{k}</span>
                        <span className="font-display text-lg text-terracotta">{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex gap-4">
                <Phone className="text-olive shrink-0 mt-1" size={22} />
                <div>
                  <p className="font-body text-xs uppercase tracking-widest text-ink/45 mb-2">
                    Call / Order
                  </p>
                  <a
                    data-testid="visit-phone-link"
                    href={`tel:${RESTAURANT.phone.replace(/\s/g, "")}`}
                    className="font-display text-3xl text-ink hover:text-olive transition-colors"
                  >
                    {RESTAURANT.phone}
                  </a>
                  <p className="flex items-center gap-2 mt-3 font-body text-sm text-olive">
                    <Leaf size={16} /> 100% Pure Vegetarian
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Map */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overflow-hidden rounded-2xl border border-line h-[420px] lg:h-full min-h-[420px] shadow-[0_30px_60px_rgba(78,98,44,0.10)]">
                <iframe
                  title="Udupi Paakamane location map"
                  data-testid="visit-map"
                  src={mapEmbed}
                  className="w-full h-full grayscale-[0.15]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
